import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { shizaiApi } from '../shizaiApi';
import { WorkOrderPicker } from './WorkOrderPicker';
import { DeliveryDestPicker } from './DeliveryDestPicker';
import { LineCard } from './LineCard';
import { NONE, emptyForm, emptyLine, validateLines, validateHeader, toLineBody, isAllInHouse } from './shared';
import { peekOrderPrefill, clearOrderPrefill } from '@/lib/assetProcurementApi';

/* Required-field marker */
const Req = () => <span className="text-rose-500">＊</span>;
const FieldError = ({ msg, testId }) =>
  msg ? (
    <p className="mt-1 text-[11px] text-rose-600 font-medium" data-testid={testId}>
      {msg}
    </p>
  ) : null;

/* ─── New request form (shopping-list style) ─── */
export function NewRequest({ categories, onSubmitted }) {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({}); // header field errors
  const [showErrors, setShowErrors] = useState(false);
  // 台帳の「この資産を発注」から来たときは1行目をその資産で埋める。
  // 既存のマスター選択（item_id + item_source）の形で入れるので、入荷時の
  // 原価反映はいつもの経路（_find_master_match）がそのまま使える。
  // 読むだけにして、実際に1行目へ入れられたときだけ消す。
  // 画面遷移の途中で一度作り直されると、消費済みで1行目が空のまま開いてしまう。
  const [prefilled] = useState(() => {
    const p = peekOrderPrefill();
    return p?.item_id ? p : null;
  });
  useEffect(() => {
    if (!prefilled) return;
    // まとめ売りの品（1セット=30本）は、数量を在庫単位でそのまま入れる。
    // 発注する人が「1セット」を「1本」と書いてしまう事故を防ぐため。
    const pack = Number(prefilled.pack_qty) || 0;
    clearOrderPrefill();
    toast.info(`${prefilled.name || '資産'} を1行目に入れました`);
    setForm((f) => ({
      ...f,
      lines: [{
        ...emptyLine(), ...prefilled, at: undefined,
        qty: pack > 0 ? pack : 1,
      }, ...f.lines.slice(1)],
    }));
  }, [prefilled]);

  // 入数のヒント（2セット = 60本）。発注数量を在庫単位で見せたまま、何セット分かを添える。
  const packHint = (() => {
    const pack = Number(prefilled?.pack_qty) || 0;
    if (pack <= 0) return null;
    const qty = Number(form.lines?.[0]?.qty) || 0;
    const uom = form.lines?.[0]?.uom || '個';
    const pu = prefilled.purchase_unit || 'パック';
    const packs = qty > 0 ? Math.round((qty / pack) * 100) / 100 : 0;
    return `${prefilled.pack_label || `1${pu} = ${pack}${uom}`}｜${packs}${pu} = ${qty}${uom}`;
  })();

  const clearErr = (key) => {
    if (!errors[key]) return;
    setErrors((prev) => {
      const n = { ...prev }; delete n[key]; return n;
    });
  };

  const setLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    }));
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) => setForm((f) => ({
    ...f,
    lines: f.lines.length === 1 ? [emptyLine()] : f.lines.filter((_, i) => i !== idx),
  }));

  // 全部 自社製作 → REQ は作らず技術の 製作依頼 だけになる
  const allInHouse = isAllInHouse(form.lines);

  const submit = async () => {
    // 1) header validation（全部 自社製作 なら 受注品名・分類・納入先 は聞かない）
    const headerErrs = validateHeader(form, allInHouse);
    setErrors(headerErrs);
    setShowErrors(true);
    if (Object.keys(headerErrs).length > 0) {
      toast.error('必須項目を入力してください');
      return;
    }
    // 2) lines validation
    const v = validateLines(form.lines);
    if (v.error) {
      toast.error(v.error);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        needed_by: form.needed_by || null,
        priority: form.priority,
        order_name: form.order_name || null,
        order_category_id: form.order_category_id || null,
        delivery_destination_id: form.delivery_destination?.id || null,
        mfg_order_id: form.mfg_order_id || null,
        is_tax_inclusive: !!form.is_tax_inclusive,
        notes: form.notes || null,
        lines: v.lines.map(toLineBody),
      };
      const created = await shizaiApi.createMyRequest(body);
      const fabNos = (created.fabrications || []).map((f) => f.fab_no).join('、');
      toast.success(created.request_no
        ? `依頼を送信しました (${created.request_no})${fabNos ? ` / 製作依頼 ${fabNos}` : ''}`
        : `技術へ製作依頼を送りました (${fabNos})`);
      setForm(emptyForm());
      setErrors({});
      setShowErrors(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const err = (k) => (showErrors ? errors[k] : null);
  const errCls = (k) => (err(k) ? 'border-rose-400 focus-visible:ring-rose-300' : '');

  return (
    <div className="space-y-5">
      {/* Header card: 希望納期 / 優先度 / 受注品名 / 分類 / 納入先 / 作業指示 / 備考 */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5">
        {allInHouse && (
          <p className="mb-3 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-[11px] text-teal-900"
            data-testid="pr-all-in-house-hint">
            すべての行が 自社製作 です。REQ（TECHS への発注）は作らず、技術の 製作依頼 だけを作ります。
            お客様向けの 受注品名・分類・納入先 は要りません（行ごとの納入先を使います）。
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-semibold text-zinc-600">希望納期 <Req /></Label>
            <DateField
              value={form.needed_by || ''}
              onChange={(v) => { setForm((f) => ({ ...f, needed_by: v })); clearErr('needed_by'); }}
              className="mt-1"
              inputClassName={`h-10 ${errCls('needed_by')}`}
              warnNonWorking
              testId="pr-needed-by"
            />
            <FieldError msg={err('needed_by')} testId="pr-err-needed-by" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">優先度</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="h-10 mt-1" data-testid="pr-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">低</SelectItem>
                <SelectItem value="normal">通常</SelectItem>
                <SelectItem value="high">高（至急）</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">受注品名 {!allInHouse && <Req />}</Label>
            <Input
              value={form.order_name}
              onChange={(e) => { setForm((f) => ({ ...f, order_name: e.target.value })); clearErr('order_name'); }}
              placeholder="例: XYZ製品-2026-01"
              className={`h-10 mt-1 ${errCls('order_name')}`}
              data-testid="pr-order-name"
            />
            <FieldError msg={err('order_name')} testId="pr-err-order-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">分類 {!allInHouse && <Req />}</Label>
            <Select
              value={form.order_category_id || NONE}
              onValueChange={(v) => {
                setForm((f) => ({ ...f, order_category_id: v === NONE ? '' : v }));
                if (v !== NONE) clearErr('order_category_id');
              }}
            >
              <SelectTrigger
                className={`h-10 mt-1 ${errCls('order_category_id')}`}
                data-testid="pr-order-category"
              >
                <SelectValue placeholder="選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {(categories || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.symbol ? ` (${c.symbol})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const sel = (categories || []).find((c) => c.id === form.order_category_id);
              return sel?.group_name ? (
                <p className="text-[10px] text-sky-600 mt-1" data-testid="pr-category-dept-hint">
                  この分類の依頼は「{sel.group_name}」の担当者へ通知されます
                </p>
              ) : null;
            })()}
            <FieldError msg={err('order_category_id')} testId="pr-err-order-category" />
          </div>
          <div className="col-span-2 sm:col-span-2">
            <Label className="text-xs font-semibold text-zinc-600">
              納入先 {!allInHouse && <Req />} <span className="text-zinc-400 font-normal">(納入先マスターから選択)</span>
            </Label>
            <div className={err('delivery_destination') ? 'rounded-md ring-1 ring-rose-300' : ''}>
              <DeliveryDestPicker
                value={form.delivery_destination}
                onChange={(dd) => { setForm((f) => ({ ...f, delivery_destination: dd })); if (dd?.id) clearErr('delivery_destination'); }}
                testId="pr-delivery-dest"
              />
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">※ 発注前（発注済にする前）に調達方法と納入先の設定が必要です</p>
            <FieldError msg={err('delivery_destination')} testId="pr-err-delivery-dest" />
          </div>
          <div className="col-span-2 sm:col-span-4">
            <Label className="text-xs font-semibold text-zinc-600">
              作業指示 <span className="text-zinc-400 font-normal">(任意 — 関連する製造オーダーがあれば紐付け)</span>
            </Label>
            <WorkOrderPicker
              value={form.mfg_order_id}
              valueLabel={form.mfg_order_label}
              onChange={(id, label) => setForm((f) => ({ ...f, mfg_order_id: id, mfg_order_label: label }))}
            />
          </div>
          <div className="col-span-2 sm:col-span-4">
            <Label className="text-xs font-semibold text-zinc-600">依頼備考（全体）</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="例: 現場のライン2用"
              className="h-10 mt-1"
              data-testid="pr-notes"
            />
          </div>
        </div>
      </div>

      {/* Lines */}
      {packHint && (
        <p className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-[11px] font-semibold text-teal-800"
          data-testid="pr-pack-hint">
          数量は在庫単位（{form.lines?.[0]?.uom || '個'}）で入れてください — {packHint}
        </p>
      )}
      <div className="space-y-3" data-testid="pr-lines">
        {form.lines.map((ln, i) => (
          <LineCard
            key={ln._key}
            line={ln}
            index={i}
            canRemove={form.lines.length > 1}
            onChange={(patch) => setLine(i, patch)}
            onRemove={() => removeLine(i)}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="outline" onClick={addLine} className="h-11" data-testid="pr-add-line">
          <Plus size={16} weight="bold" className="mr-1.5" /> 品目を追加
        </Button>

        <Button
          onClick={submit}
          disabled={submitting}
          className="h-11 px-6 bg-teal-700 hover:from-orange-600 hover:to-rose-700 text-white shadow-md"
          data-testid="pr-submit"
        >
          <PaperPlaneTilt size={16} weight="fill" className="mr-2" />
          {submitting ? '送信中...' : allInHouse ? '技術へ製作依頼を送る' : '資材担当へ依頼を送る'}
        </Button>
      </div>
    </div>
  );
}
